import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';

import { DatePickerNew, Input, Select } from 'common-src/components/base';
import { booleanOptions } from 'common-src/constants/booleanOptions';
import {
  CaregiverMedicareStatus,
  CaregiverMedicareStatusOptions,
  CaregiverPhoneTypeOptions,
  CaregiverPreferredLanguageOptions,
  CaregiverRelationshipOptions,
  CaregiverSexOptions,
  TechComfortLevelOptions,
} from 'common-src/models/PatientCaregiver';
import { getFormattedPhoneNumber } from 'common-src/models/PatientPhoneNumber/helpers';
import { cleanedText, PHONE_MASK } from 'common-src/presenters';

import styles from './Caregivers.module.scss';

const CaregiverInlineForm = ({ data, errors, isEditMode, isGuide, onChange }) => {
  const renderCoreFields = () => (
    <div className={styles.grid}>
      <Input
        id="caregiver-first-name"
        label="First Name"
        value={data.firstName}
        onTextChange={(value) => onChange('firstName', value)}
        placeholder="Enter first name"
        required
        readOnly={!isEditMode}
        size="small"
        errorText={errors.firstName}
      />
      <Input
        id="caregiver-last-name"
        label="Last Name"
        value={data.lastName}
        onTextChange={(value) => onChange('lastName', value)}
        placeholder="Enter last name"
        required
        readOnly={!isEditMode}
        size="small"
        errorText={errors.lastName}
      />
      <Select
        id="caregiver-is-primary"
        label="Is primary?"
        options={booleanOptions}
        onChange={(op) => onChange('isPrimary', op?.value)}
        value={booleanOptions.find((option) => option.value === data.isPrimary)}
        placeholder="Select"
        required
        readOnly={!isEditMode}
        size="small"
        position="absolute"
        errorText={errors.isPrimary}
      />
      <Input
        id="caregiver-email"
        label="Email address"
        value={data.email}
        onTextChange={(value) => onChange('email', value)}
        placeholder="Enter email"
        required
        readOnly={!isEditMode}
        size="small"
        errorText={errors.email}
      />
      <DatePickerNew
        id="caregiver-date-of-birth"
        minDate={undefined}
        maxDate={new Date()}
        placeholder="Choose date of birth"
        header="Date of birth"
        value={data.dateOfBirth || null}
        onDateSelected={(date) =>
          onChange('dateOfBirth', date ? moment(date).format('YYYY-MM-DD') : null)
        }
        onBlur={(date) => {
          if (date === undefined) {
            onChange('dateOfBirth', data.dateOfBirth, 'Invalid date format');
            return;
          }
          onChange('dateOfBirth', data.dateOfBirth, '');
        }}
        required={isGuide}
        readOnly={!isEditMode}
        size="small"
        errorText={errors.dateOfBirth}
      />
      <Input
        id="caregiver-phone"
        label="Phone Number"
        value={data.phone ? getFormattedPhoneNumber(data.phone) : ''}
        onTextChange={(value) => onChange('phone', cleanedText(value))}
        mask={PHONE_MASK}
        placeholder="Enter phone number"
        required
        readOnly={!isEditMode}
        size="small"
        errorText={errors.phone}
      />
      <Select
        id="caregiver-relationship"
        label="Relationship"
        options={CaregiverRelationshipOptions}
        onChange={(op) => onChange('relationship', op?.value)}
        value={CaregiverRelationshipOptions.find((op) => op.value === data.relationship)}
        placeholder="Select relationship"
        required
        readOnly={!isEditMode}
        size="small"
        position="absolute"
        errorText={errors.relationship}
      />
      <Select
        id="caregiver-medicare-status"
        label="Medicare status"
        options={CaregiverMedicareStatusOptions}
        onChange={(op) => onChange('medicareStatus', op?.value)}
        value={CaregiverMedicareStatusOptions.find((op) => op.value === data.medicareStatus)}
        placeholder="Select medicare status"
        required={isGuide}
        readOnly={!isEditMode}
        size="small"
        position="absolute"
        errorText={errors.medicareStatus}
      />
    </div>
  );

  const renderExtendedFields = () => {
    if (!isEditMode) return null;

    return (
      <div className={styles.grid}>
        <Select
          id="caregiver-phone-type"
          label="Phone Type"
          options={CaregiverPhoneTypeOptions}
          onChange={(op) => onChange('phoneType', op?.value)}
          value={CaregiverPhoneTypeOptions.find((op) => op.value === data.phoneType)}
          placeholder="Select phone type"
          required
          size="small"
          position="absolute"
          errorText={errors.phoneType}
        />
        <Select
          id="caregiver-sex"
          label="Sex"
          options={CaregiverSexOptions}
          onChange={(op) => onChange('sex', op?.value)}
          value={CaregiverSexOptions.find((op) => op.value === data.sex)}
          placeholder="Select sex"
          required={isGuide}
          size="small"
          position="absolute"
          errorText={errors.sex}
        />
        <Select
          id="caregiver-lives-with-patient"
          label="Lives with patient?"
          options={booleanOptions}
          onChange={(op) => onChange('livesWithPatient', op?.value)}
          value={booleanOptions.find((option) => option.value === data.livesWithPatient)}
          placeholder="Select"
          required
          size="small"
          position="absolute"
          errorText={errors.livesWithPatient}
        />
        <Input
          id="caregiver-mbi"
          label="MBI Number"
          value={data.mbi}
          onTextChange={(value) => onChange('mbi', value)}
          placeholder="Enter medicare number"
          required={data.medicareStatus === CaregiverMedicareStatus.Yes}
          size="small"
          errorText={errors.mbi}
        />
        <Input
          id="caregiver-role-years"
          label="Role Years"
          type="number"
          value={typeof data.roleYears === 'number' ? data.roleYears : ''}
          onTextChange={(value) => onChange('roleYears', typeof value !== 'number' ? null : value)}
          placeholder="Enter years"
          required={isGuide && typeof data.roleMonths !== 'number'}
          size="small"
          errorText={errors.roleYears}
        />
        <Input
          id="caregiver-role-months"
          label="Role Months"
          type="number"
          value={typeof data.roleMonths === 'number' ? data.roleMonths : ''}
          onTextChange={(value) => onChange('roleMonths', typeof value !== 'number' ? null : value)}
          placeholder="Enter months"
          required={isGuide && typeof data.roleYears !== 'number'}
          size="small"
          errorText={errors.roleMonths}
        />
        <Select
          id="caregiver-preferred-language"
          label="Preferred Language"
          options={CaregiverPreferredLanguageOptions}
          onChange={(op) => onChange('preferredLanguage', op?.value)}
          value={CaregiverPreferredLanguageOptions.find(
            (op) => op.value === data.preferredLanguage,
          )}
          placeholder="Select preferred language"
          required
          size="small"
          position="absolute"
          errorText={errors.preferredLanguage}
        />
        <Select
          id="caregiver-tech-comfort-level"
          label="Tech Comfort Level"
          options={TechComfortLevelOptions}
          onChange={(op) => onChange('techComfortLevel', op?.value)}
          value={TechComfortLevelOptions.find((op) => op.value === data.techComfortLevel)}
          placeholder="Select tech comfort level"
          required
          size="small"
          position="absolute"
          errorText={errors.techComfortLevel}
        />
      </div>
    );
  };

  return (
    <>
      {renderCoreFields()}
      {renderExtendedFields()}
    </>
  );
};

CaregiverInlineForm.propTypes = {
  data: PropTypes.exact({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    isPrimary: PropTypes.bool,
    email: PropTypes.string,
    phone: PropTypes.string,
    phoneType: PropTypes.string,
    dateOfBirth: PropTypes.string,
    sex: PropTypes.string,
    relationship: PropTypes.string,
    livesWithPatient: PropTypes.bool,
    medicareStatus: PropTypes.string,
    mbi: PropTypes.string,
    roleYears: PropTypes.number,
    roleMonths: PropTypes.number,
    preferredLanguage: PropTypes.string,
    techComfortLevel: PropTypes.string,
  }).isRequired,
  errors: PropTypes.exact({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    isPrimary: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    phoneType: PropTypes.string,
    dateOfBirth: PropTypes.string,
    sex: PropTypes.string,
    relationship: PropTypes.string,
    livesWithPatient: PropTypes.string,
    medicareStatus: PropTypes.string,
    mbi: PropTypes.string,
    roleYears: PropTypes.string,
    roleMonths: PropTypes.string,
    preferredLanguage: PropTypes.string,
    techComfortLevel: PropTypes.string,
  }),
  isEditMode: PropTypes.bool,
  isGuide: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

CaregiverInlineForm.defaultProps = {
  errors: {},
  isEditMode: false,
  isGuide: false,
};

export default CaregiverInlineForm;
